import { DateTime } from 'luxon';
import React, { useEffect, useState, useMemo } from 'react';
import { isDevFlag } from '@americanexpress/conditional-features';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Button } from '@americanexpress/dls-react';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { DEFAULT_DATE_FORMAT } from '../../constants/dateFormatConstants';
import {
  getEndDate,
  getFocusedField,
  getMaxDaysAdvanceFromToday,
  getMinShownDateString,
  getStartDate,
} from '../../selectors/dateSelectors';
import CalendarMonth from '../CalendarMonth/CalendarMonth';
import { useDateSelector } from '../../context/dateContext';
import { MONTHS, START_DATE } from '../../constants/dateConstants';
import { MONTHS_TO_DISPLAY_MOBILE } from '../../constants/monthConstants';
import themeOverrides from '../themeOverrides.scss';
import { setCalendarCurrentDate } from '../../utils/dateUtils';

const CalendarVertical = () => {
  const minShownDateString = useDateSelector(getMinShownDateString);
  const luxonMinDate = useMemo(
    () => DateTime.fromFormat(minShownDateString, DEFAULT_DATE_FORMAT),
    [minShownDateString]
  );
  const hasShowMoreEnabled = isDevFlag('mobileShowMoreDatepicker');
  const maxDaysAdvanceFromToday = useDateSelector(getMaxDaysAdvanceFromToday);
  const isMobile = useMediaBreakpoint('sm-down');

  const maximumMonthsToShow = Math.ceil(maxDaysAdvanceFromToday / 31) + 1;
  const monthsDisplayed = hasShowMoreEnabled
    ? MONTHS_TO_DISPLAY_MOBILE
    : maximumMonthsToShow;
  const [monthsToShow, incrementMonths] = useState(monthsDisplayed);
  const focusedField = useDateSelector(getFocusedField);
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);

  const { formatMessage } = useIntl();

  const showMoreDates = () => {
    const totalMonthsShown = monthsToShow + MONTHS_TO_DISPLAY_MOBILE;
    if (totalMonthsShown <= maximumMonthsToShow) {
      incrementMonths(totalMonthsShown);
    } else {
      incrementMonths(
        totalMonthsShown - (totalMonthsShown - maximumMonthsToShow)
      );
    }
  };

  useEffect(() => {
    if (isMobile) {
      const currentDate = setCalendarCurrentDate(
        focusedField,
        startDate,
        endDate
      );
      if (currentDate) {
        const currentLuxonDate = DateTime.fromFormat(
          currentDate,
          DEFAULT_DATE_FORMAT
        );
        const monthsDifference = currentLuxonDate?.diff(
          luxonMinDate.startOf('month'),
          MONTHS
        ).months;
        if (monthsDifference > monthsToShow) {
          incrementMonths(Math.ceil(monthsDifference));
        }
      }
    }
  }, [startDate, endDate, focusedField]);

  useEffect(() => {
    const onKeyUpListener = (event) => {
      if (isMobile && (event.keyCode >= 38 || event.keyCode <= 40)) {
        const currentDate = focusedField === START_DATE ? startDate : endDate;
        if (currentDate) {
          const currentLuxonDate = DateTime.fromFormat(
            currentDate,
            DEFAULT_DATE_FORMAT
          );
          const monthsDifference = currentLuxonDate?.diff(
            luxonMinDate,
            MONTHS
          ).months;
          if (monthsDifference > monthsToShow) {
            incrementMonths(Math.ceil(monthsDifference));
          }
        }
      }
    };
    document.addEventListener('keyup', onKeyUpListener);
    return () => document.removeEventListener('keyup', onKeyUpListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on render
  }, []);

  return (
    <div
      className="flex flex-column width-full"
      data-testid="date-picker-calendar"
    >
      {[...new Array(monthsToShow).keys()].map((monthKey) => (
        <CalendarMonth
          key={monthKey}
          index={monthKey}
          shownDate={luxonMinDate.plus({ months: monthKey })}
        />
      ))}
      {hasShowMoreEnabled && monthsToShow < maximumMonthsToShow && (
        <Button
          data-testid="calendar-vertical-button"
          onClick={showMoreDates}
          type="button"
          className={clsx('fluid margin-t', themeOverrides.secondaryButton)}
          tabIndex="-1"
          variant="secondary"
        >
          {formatMessage({ id: 'show_more_dates' })}
        </Button>
      )}
    </div>
  );
};

export default CalendarVertical;
